import {
  BoundingBoxType,
  LatLngType,
  ShapeFilter,
  ShapeFilterType,
} from "../ts-types/mapsTypes";
import {
  PingQueryParameterType,
  pushQueryParameters,
  replaceQueryParameters,
} from "./setQueryParameter";
import { HistoryAdapter } from "./context";
import {
  COUNTRY_CODE_TO_NAME_MAPPING,
  STATE_TO_NAME_MAPPING,
} from "./report-utils";
import { formatCurrency } from "./formatters";
import {
  CENTER_DECIMALS_USED,
  GEOCODING_CONFIDENCE_MAPPING,
} from "./constants";
import {
  FREE_PLAN_LOOKUP_SOURCES,
  ORG_LOOKUP_SOURCES,
  PAID_PLAN_LOOKUP_SOURCES,
  SOURCES,
} from "../constants/maps-constants";
import { isNumber } from "lodash";
import {
  getH3ResolutionAreaLabel,
  getH3ResolutionFromFilterField,
} from "./h3-utils";

const CIRCLE_SEGMENTS = 36;

export const calculateBoundingBox = (shape: ShapeFilter) => {
  if (shape.type === ShapeFilterType.RECTANGLE) {
    return {
      lat1: Math.max(shape.corner1.lat, shape.corner2.lat),
      lng1: Math.min(shape.corner1.lng, shape.corner2.lng),
      lat2: Math.min(shape.corner1.lat, shape.corner2.lat),
      lng2: Math.max(shape.corner1.lng, shape.corner2.lng),
    };
  } else if (shape.type === ShapeFilterType.POLYGON) {
    const lats = shape.points.map((p) => p.lat);
    const lngs = shape.points.map((p) => p.lng);
    return {
      lat1: Math.max(...lats),
      lng1: Math.min(...lngs),
      lat2: Math.min(...lats),
      lng2: Math.max(...lngs),
    };
  } else if (shape.type === ShapeFilterType.CIRCLE) {
    const latDelta = shape.radius / 111320; // Convert meters to degrees (approx)
    const lngDelta = latDelta / Math.cos((shape.center.lat * Math.PI) / 180);

    return {
      lat1: shape.center.lat + latDelta,
      lng1: shape.center.lng - lngDelta,
      lat2: shape.center.lat - latDelta,
      lng2: shape.center.lng + lngDelta,
    };
  }
};

export const getBoundingPolygon = (shape: ShapeFilter): string | undefined => {
  if (shape.type === ShapeFilterType.RECTANGLE) {
    const points = [
      shape.corner1,
      { lat: shape.corner1.lat, lng: shape.corner2.lng },
      shape.corner2,
      { lat: shape.corner2.lat, lng: shape.corner1.lng },
      shape.corner1,
    ];
    return points.map((p) => `${p.lat},${p.lng}`).join(",");
  } else if (shape.type === ShapeFilterType.POLYGON) {
    const points = [...shape.points];
    if (
      points[0].lat !== points[points.length - 1].lat ||
      points[0].lng !== points[points.length - 1].lng
    ) {
      points.push(points[0]);
    }
    return points.map((p) => `${p.lat},${p.lng}`).join(",");
  } else if (shape.type === ShapeFilterType.CIRCLE) {
    const points = [];
    for (let i = 0; i < CIRCLE_SEGMENTS; i++) {
      const angle = (2 * Math.PI * i) / CIRCLE_SEGMENTS;
      const latDelta = (shape.radius / 111320) * Math.cos(angle);
      const lngDelta =
        ((shape.radius / 111320) * Math.sin(angle)) /
        Math.cos((shape.center.lat * Math.PI) / 180);

      points.push({
        lat: shape.center.lat + latDelta,
        lng: shape.center.lng + lngDelta,
      });
    }
    points.push(points[0]);
    return points.map((p) => `${p.lat},${p.lng}`).join(",");
  }
  return undefined;
};

export const parseLatLng = (coord: string): LatLngType | null => {
  const coordParts = coord.split(/[\s,]+/).filter(Boolean);
  if (coordParts.length !== 2) return null;

  const [lat, lng] = coordParts.map((c) => Number(c));
  if (!Number.isFinite(lat) || Math.abs(lat) > 90) return null;
  if (!Number.isFinite(lng) || Math.abs(lng) > 180) return null;
  return { lat, lng };
};

export const parseShapeFilter = (
  searchParams: URLSearchParams,
): ShapeFilter | null => {
  const type = searchParams.get("shapeType") as ShapeFilterType;
  if (!type) return null;

  switch (type) {
    case ShapeFilterType.RECTANGLE: {
      const corner1 = parseLatLng(searchParams.get("sketch1") || "");
      const corner2 = parseLatLng(searchParams.get("sketch2") || "");
      if (!corner1 || !corner2) return null;
      return { type, corner1, corner2 };
    }

    case ShapeFilterType.CIRCLE: {
      const center = parseLatLng(searchParams.get("sketch1") || "");
      const radius = parseFloat(searchParams.get("radius") || "");
      if (!center || !radius) return null;
      return { type, center, radius };
    }

    case ShapeFilterType.POLYGON: {
      const points: LatLngType[] = [];
      let index = 1;
      let point: string | null;

      while ((point = searchParams.get(`sketch${index}`))) {
        const latLng = parseLatLng(point);
        if (!latLng) return null;
        points.push(latLng);
        index++;
      }

      return points.length >= 3 ? { type, points } : null;
    }

    default:
      return null;
  }
};

export const setShapeFilter = (
  searchParams: URLSearchParams,
  history: HistoryAdapter,
  shape: ShapeFilter | null,
  replace: boolean = true,
) => {
  const params: Record<string, string> = {
    shapeType: "",
    sketch1: "",
    sketch2: "",
    radius: "",
  };

  let index = 3;
  while (searchParams.has(`sketch${index}`)) {
    params[`sketch${index}`] = "";
    index++;
  }

  if (shape) {
    params.shapeType = shape.type;

    switch (shape.type) {
      case ShapeFilterType.RECTANGLE:
        params.sketch1 = `${shape.corner1.lat},${shape.corner1.lng}`;
        params.sketch2 = `${shape.corner2.lat},${shape.corner2.lng}`;
        break;

      case ShapeFilterType.CIRCLE:
        params.sketch1 = `${shape.center.lat},${shape.center.lng}`;
        params.radius = shape.radius.toString();
        break;

      case ShapeFilterType.POLYGON:
        shape.points.forEach((point, index) => {
          params[`sketch${index + 1}`] = `${point.lat},${point.lng}`;
        });
        break;
    }
  }

  const method = replace ? replaceQueryParameters : pushQueryParameters;
  method(searchParams, history, params);
};

export const checkShapeFilterActive = (shape: ShapeFilter | null): boolean => {
  if (!shape) return false;

  switch (shape.type) {
    case ShapeFilterType.RECTANGLE:
      return Boolean(shape.corner1 && shape.corner2);
    case ShapeFilterType.CIRCLE:
      return Boolean(shape.center && shape.radius);
    case ShapeFilterType.POLYGON:
      return shape.points.length >= 3;
    default:
      return false;
  }
};

const formatLayerMillions = (amount: number): string => String(amount / 1_000_000);

const getLayerChipLabel = (attach: number, layerLimit: number): string => {
  const attachAmount = Number.isFinite(attach) ? attach : 0;

  if (!Number.isFinite(layerLimit) || layerLimit <= 0) {
    return `Layer - Excess $${formatLayerMillions(attachAmount)}`;
  }

  if (attachAmount === 0) {
    return `Layer - Primary ${formatLayerMillions(layerLimit)}`;
  }

  return `Layer - ${formatLayerMillions(layerLimit)}x${formatLayerMillions(attachAmount)}`;
};

export const getFilterChipLabel = (
  key: string,
  value: PingQueryParameterType,
): string => {
  if (key === "limits__total_limit__gt") {
    return `TIV > ${formatCurrency(value)}`;
  } else if (key === "limits__total_limit__lte") {
    return `TIV ≤ ${formatCurrency(value)}`;
  } else if (key === "limits__total_limit__gte") {
    return `TIV ≥ ${formatCurrency(value)}`;
  } else if (key === "cape_roof_condition_score") {
    if (String(value) === "-1") {
      return "CAPE Score - Poor (-1)";
    } else if (String(value) === "-2") {
      return "CAPE Score - Severe (-2)";
    }
    return `CAPE Score - ${value}`;
  } else if (key === "slosh_zone") {
    return `SLOSH - Zone ${value}`;
  } else if (key === "layer_type") {
    const layerTypeLabels: Record<string, string> = { primary: "Primary", excess: "Excess" };
    return `Layers - ${layerTypeLabels[String(value)] ?? value}`;
  } else if (key === "wind_tier") {
    return `Hurricane - ${value}`;
  } else if (key === "orig_sov_geocoding_disagreement__gte") {
    return `Displacement ≥ ${value}m`;
  } else if (key === "distance_to_coast_miles__lte") {
    return `DTC ≤ ${value} mi`;
  } else if (key === "distance_to_coast_miles__lt") {
    return `DTC < ${value} mi`;
  } else if (key === "distance_to_coast_miles__gt") {
    return `DTC > ${value} mi`;
  } else if (key === "distance_to_coast_miles__gte") {
    return `DTC ≥ ${value} mi`;
  } else if (
    key === "distance_to_coast_miles__gt_or_null" ||
    key === "distance_to_coast_miles__gte_or_null"
  ) {
    return `DTC - Inland`;
  } else if (key === "wfir_riskr") {
    return `NRI Wildfire - ${value}`;
  } else if (key === "rps_risk_level") {
    return `USDA Enhanced - ${value}`;
  } else if (key === "hail_riskr") {
    return `NRI Hail - ${value}`;
  } else if (key === "trnd_riskr") {
    return `NRI Tornado - ${value}`;
  } else if (key === "cfld_riskr") {
    return `NRI Coastal Flood - ${value}`;
  } else if (key === "total_crime_grade") {
    return `EASI Crime - ${value}`;
  } else if (key === "aais_classification") {
    return `AAIS - ${value}`;
  } else if (key === "const__bldg_year_built") {
    return `Year Built - ${value}`;
  } else if (key === "const__bldg_year_built__lte") {
    return `Year Built ≤ ${value}`;
  } else if (key === "const__bldg_year_built__gt") {
    return `Year Built > ${value}`;
  } else if (key === "const__bldg_year_built__lte_or_null") {
    return `Year Built ≤ ${value} or N/A`;
  } else if (key === "eq_zone") {
    return `EQ Zone - ${value}`;
  } else if (key === "ping_geo__match_level") {
    const chipValue = (GEOCODING_CONFIDENCE_MAPPING as Record<string, string>)[
      value as string
    ];
    return chipValue ? `Geocoding - ${chipValue}` : `Geocoding - ${value}`;
  } else if (key === "protect__pct_sprinklered") {
    return `Sprinklered - ${value}`;
  } else if (key === "protect__sprinklers") {
    return `Sprinklers - ${value}`;
  } else if (key === "const__num_stories") {
    return `Num of Stories - ${value}`;
  } else if (key === "const__num_stories__lte") {
    return `Num of Stories ≤ ${value}`;
  } else if (key === "const__num_stories__gt") {
    return `Num of Stories > ${value}`;
  } else if (key === "const__bldg_area") {
    return `Area - ${value} sf`;
  } else if (key === "const__bldg_area__gt") {
    return `Area > ${value} sf`;
  } else if (key === "const__bldg_area__gte") {
    return `Area ≥ ${value} sf`;
  } else if (key === "const__bldg_area__lte") {
    return `Area ≤ ${value} sf`;
  } else if (key === "const__bldg_area__lte_or_null") {
    return `Area ≤ ${value} sf or N/A`;
  } else if (key === "const__iso_protection_class") {
    return `Protection Class - ${value}`;
  } else if (key === "const__iso_protection_class__gt") {
    return `Protection Class > ${value}`;
  } else if (key === "const__iso_protection_class__gte") {
    return `Protection Class ≥ ${value}`;
  } else if (key === "const__iso_protection_class__lte") {
    return `Protection Class ≤ ${value}`;
  } else if (key === "const__roof_year_updated") {
    return `Roof Year - ${value}`;
  } else if (key === "const__roof_year_updated__lte_or_null") {
    return `Roof Year ≤ ${value} or N/A`;
  } else if (key === "scs_roof_age") {
    return `Roof Year ≤ ${value}`;
  } else if (key === "state") {
    return `State - ${(STATE_TO_NAME_MAPPING as Record<string, string>)[value as string] || value}`;
  } else if (key === "country") {
    return `Country - ${(COUNTRY_CODE_TO_NAME_MAPPING as Record<string, string>)[value as string] || value}`;
  } else if (key === "county") {
    return `County - ${value}`;
  }

  const h3Resolution = getH3ResolutionFromFilterField(key);
  if (h3Resolution) {
    return `H3 Hex - ${getH3ResolutionAreaLabel(h3Resolution)}`;
  } else if (key === "fema_flood_zone") {
    return `FEMA Flood - ${value}`;
  } else if (key === "fema_flood_zone__bins") {
    return `FEMA Flood - ${value}`;
  } else if (key === "wildfire_description") {
    return `Wildfire - ${value}`;
  } else if (key === "fema_wildfire") {
    return `FEMA Wildfire - ${value}`;
  } else if (key === "const_code_pcg") {
    return `Construction - ${value}`;
  } else if (key === "const__desc_air") {
    return `Construction (AIR) - ${value}`;
  } else if (key === "const__desc_rms") {
    return `Construction (RMS) - ${value}`;
  } else if (key === "const__desc_iso") {
    return `Construction (ISO) - ${value}`;
  } else if (key === "const__desc_ping") {
    return `Construction (Ping) - ${value}`;
  } else if (key === "const__code_air") {
    return `Construction Code (AIR) - ${value}`;
  } else if (key === "const__code_rms") {
    return `Construction Code (RMS) - ${value}`;
  } else if (key === "const__code_iso") {
    return `Construction Code (ISO) - ${value}`;
  } else if (key === "occupancy__desc_ping") {
    return `Occupancy - ${value}`;
  } else if (key === "occupancy__desc_air") {
    return `Occupancy (AIR) - ${value}`;
  } else if (key === "occupancy__desc_atc") {
    return `Occupancy (ATC) - ${value}`;
  } else if (key === "occupancy__code_air") {
    return `Occupancy Code (AIR) - ${value}`;
  } else if (key === "occupancy__code_atc") {
    return `Occupancy Code (ATC) - ${value}`;
  } else if (key === "layer_limit") {
    return `Layer Limit - ${formatCurrency(value)}`;
  } else if (key === "attach") {
    return `Attachment - ${formatCurrency(value)}`;
  } else if (key === "layer_analysis") {
    // x200_plus bucket from layer analysis
    return `Layer - Excess $200`;
  } else if (key === "layer") {
    const [limitPart, attachPart] = String(value ?? "").split(":::");
    return getLayerChipLabel(Number(attachPart), Number(limitPart));
  }

  return String(value ?? "");
};

export const parseCenterOrNull = (center: string | null): LatLngType | null => {
  if (center === null || center === "") {
    return null;
  }

  const [lat, lng] = center.split(",").map((coord) => {
    const parsed = parseFloat(coord);
    return isNaN(parsed) ? NaN : Number(parsed.toFixed(CENTER_DECIMALS_USED));
  });

  if (isNaN(lat) || isNaN(lng)) {
    return null;
  }

  return { lat, lng };
};

const PLI_BASE_URL = "/new/maps/pli?address=";

export const getPliLink = (address: string) => {
  return `${PLI_BASE_URL}${encodeURIComponent(address)}`;
};
const ADDITIONAL_SOURCES_MAP: Record<string, string[] | undefined> = {
  [SOURCES.REGEOCODE]: [
    SOURCES.PING_GEOCODING_BETA,
    SOURCES.DISTANCE_TO_COAST,
    SOURCES.PING_SLOSH_ZONE,
    // SOURCES.DISTANCE_TO_SINKHOLE,
  ],
  [SOURCES.PLI_OUTLINES]: [SOURCES.LIGHTBOX, SOURCES.LIGHTBOX_PARCEL],
  [SOURCES.RYAN_HAZARD_V3]: [SOURCES.RYAN_HAZARD_V3, SOURCES.RYAN_GEOCODING],
  [SOURCES.VEXCEL_DATA]: [
    SOURCES.VEXCEL_PROPERTY_DAMAGE,
    SOURCES.VEXCEL_PROPERTY_INFORMATION,
  ],
  [SOURCES.SWISSRE_ALL]: [
    SOURCES.SWISSRE_HAILSTORM,
    SOURCES.SWISSRE_LIGHTNING,
    SOURCES.SWISSRE_STORMSURGE,
    SOURCES.SWISSRE_TORNADO,
    SOURCES.SWISSRE_WILDFIRE,
    SOURCES.SWISSRE_WINDSTORM,
    SOURCES.SWISSRE_EARTHQUAKE,
    SOURCES.SWISSRE_TSUNAMI,
  ],
  [SOURCES.LIGHTBOX]: [SOURCES.LIGHTBOX, SOURCES.LIGHTBOX_GEOCODING],
  [SOURCES.TEREN4D]: [SOURCES.TEREN4D, SOURCES.GOOGLE_GEOCODING],
  [SOURCES.LIGHTBOX_PARCEL]: [
    SOURCES.LIGHTBOX_PARCEL,
    SOURCES.LIGHTBOX_GEOCODING,
  ],
  [SOURCES.TIGERRISKAIR]: [SOURCES.TIGERRISKAIR, SOURCES.GOOGLE_GEOCODING],
  [SOURCES.TIGERRISKRMS]: [SOURCES.TIGERRISKRMS, SOURCES.GOOGLE_GEOCODING],
  [SOURCES.DISTANCE_TO_SINKHOLE]: [
    SOURCES.DISTANCE_TO_SINKHOLE,
    SOURCES.GOOGLE_GEOCODING,
  ],
  [SOURCES.MESSYDATA]: [SOURCES.MESSYDATA, SOURCES.GOOGLE_GEOCODING],
  [SOURCES.PING_SLOSH_ZONE]: [
    SOURCES.PING_SLOSH_ZONE,
    SOURCES.GOOGLE_GEOCODING,
  ],
  [SOURCES.AON_ALL]: [
    SOURCES.AON_CS_EQ,
    SOURCES.AON_CS_FLD,
    SOURCES.AON_CS_SCS,
    SOURCES.AON_CS_WF,
  ],
  [SOURCES.GREEN_SHIELD_ALL]: [
    SOURCES.GREEN_SHIELD_RESILIENCY_SCORE,
    SOURCES.GREEN_SHIELD_WILDFIRE_RECURRENCE_SCORE,
  ],
};

export const APPEND_ADDITIONAL_SOURCES = (
  source: string,
  integrationSettings?: Record<string, any>,
  pingDataPlan?: string | null,
): string[] => {
  if (source === SOURCES.PLI_DATA) {
    // pingDataPlan is null for every org member by design.
    if (pingDataPlan == null) return ORG_LOOKUP_SOURCES;
    return pingDataPlan === "FREE"
      ? FREE_PLAN_LOOKUP_SOURCES
      : PAID_PLAN_LOOKUP_SOURCES;
  }

  const mapped = ADDITIONAL_SOURCES_MAP[source];
  if (!mapped) return [source];

  if (!integrationSettings) return mapped;

  // A bundled source may include sub-sources the user doesn't have access to
  // — only request the ones integrationSettings says they can access.
  return mapped.filter((s) => !!integrationSettings[s]);
};

export const isValidBoundingBox = (bbox: BoundingBoxType | null) => {
  if (!bbox) {
    return false;
  }

  if (
    !isNumber(bbox.lat1) ||
    !isNumber(bbox.lat2) ||
    !isNumber(bbox.lng1) ||
    !isNumber(bbox.lng2)
  ) {
    return false;
  }

  return true;
};

export const checkWebGLSupport = (): boolean => {
  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    return !!(gl && gl instanceof WebGLRenderingContext);
  } catch (e) {
    return false;
  }
};

export const getHexDiameter = (area: number): number => {
  return 2 * Math.sqrt((2 * area) / (3 * Math.sqrt(3)));
};

export const calculateYearBltBands = (
  yearBlt: string | number | null | undefined,
) => {
  let year;
  try {
    year = parseInt(String(yearBlt));
    if (isNaN(year)) return null;
  } catch (e) {
    return null;
  }

  if (year < 1950) return "Pre-1950";
  if (year < 1960) return "1950 - 1959";
  if (year < 1970) return "1960 - 1969";
  if (year < 1980) return "1970 - 1979";
  if (year < 1990) return "1980 - 1989";
  if (year < 2000) return "1990 - 1999";
  if (year < 2010) return "2000 - 2009";
  if (year < 2020) return "2010 - 2019";
  if (year >= 2020) return "2020+";

  return "Unknown";
};

const parseOptionalNumber = (value: string | number | null | undefined) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === "string" && value.trim()) {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : undefined;
  }

  return undefined;
};

const buildPolicyAddress = ({
  fullAddress,
  street,
  city,
  state,
  zip,
}: {
  fullAddress?: string | null;
  street?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | number | null;
}) => {
  if (typeof fullAddress === "string" && fullAddress.trim()) {
    return fullAddress.trim();
  }

  const stateAndZip = [state, zip]
    .filter(
      (value) => value !== null && value !== undefined && `${value}`.trim(),
    )
    .map(String)
    .join(" ");

  const address = [street, city, stateAndZip]
    .filter(
      (value) => value !== null && value !== undefined && `${value}`.trim(),
    )
    .map(String)
    .join(", ");

  return address || undefined;
};

export const transformData = (input: any, sync_version: number = 0): any => {
  if (!input) {
    return null;
  }

  if (sync_version === 1) {
    const constBldgYearBuilt = parseOptionalNumber(input.const_bldg_year_built);
    const constBldgArea = parseOptionalNumber(input.const_bldg_area);
    const limitsTotalLimit = parseOptionalNumber(input.limits_total_limit);
    const constNumStories = parseOptionalNumber(input.const_num_stories);
    const fullAddress = buildPolicyAddress({
      fullAddress: input.full_address,
      street: input.street ?? input.route,
      city: input.city,
      state: input.state,
      zip: input.zip_code,
    });

    return {
      lat: input.latitude,
      lng: input.longitude,
      orig_lat: input.latitude,
      orig_lng: input.longitude,
      confidence: 100,
      precision: 100,
      match_level: input.match_level ?? input.ping_geo_match_level,
      s: "",
      tiv: limitsTotalLimit ?? 0,
      uid: input.item_key,
      cd: input.const_desc_air ?? "",
      od: input.occupancy_desc_air ?? "",
      fz: input.fema_flood_zone ?? "",
      yb: constBldgYearBuilt ?? 0,
      wt: input.wind_tier ?? null,
      eqz: input.eq_zone ?? "",
      eqsz: "",
      zipcode: input.zip_code ?? "",
      ybb: calculateYearBltBands(constBldgYearBuilt),
      c: input.country ?? "",
      st: input.state ?? "",
      ns: constNumStories ?? 0,
      spr: input.protect_sprinklers ?? false,
      county: input.county ?? "",
      street: input.street ?? input.route,
      city: input.city,
      state: input.state,
      zip: input.zip_code,
      fullAddress,
      bldg_name: input.bldg_name,
      building_counter: input.building_counter,
      location_group: input.location_group,
      location_group_item: input.location_group_item,
      layer_type: input.layer_type,
      limits_total_limit: limitsTotalLimit,
      const_bldg_area: constBldgArea,
      reliability: {},
    };
  } else {
    const fullAddress = buildPolicyAddress({
      fullAddress: input.data.full_address ?? input.original?.full_address,
      street: input.data.street ?? input.street ?? input.route,
      city: input.data.city ?? input.city,
      state: input.data.state ?? input.state,
      zip: input.data.zip ?? input.zip_code,
    });

    return {
      lat: input.data.latitude,
      lng: input.data.longitude,
      orig_lat: input.original?.latitude ?? input.data.latitude,
      orig_lng: input.original?.longitude ?? input.data.longitude,
      confidence: 100,
      precision: 100,
      match_level: input.ping_geo_match_level,
      s: "",
      tiv: input.data.limits__total_limit,
      uid: input.item_key,
      cd: input.data.const__desc_ping,
      od: input.data.occupancy__desc_ping,
      fz: input.data.fema_flood_zone,
      yb: input.data.const__bldg_year_built,
      wt: input.data.wind_tier,
      eqz: input.data.eq_zone,
      eqsz: input.data.eq_sub_zone,
      zipcode: input.data.zip,
      ybb: calculateYearBltBands(input.data.const__bldg_year_built),
      c: input.data.country,
      st: input.data.state,
      ns: input.data.const__num_stories,
      spr: input.data.protect__sprinklers,
      county: input.data.county,
      street: input.data.street ?? input.street ?? input.route,
      city: input.data.city ?? input.city,
      state: input.data.state ?? input.state,
      zip: input.data.zip ?? input.zip_code,
      fullAddress,
      bldg_name: input.bldg_name,
      building_counter: input.building_counter,
      location_group: input.location_group,
      location_group_item: input.location_group_item,
      reliability: {},
      is_florida: input.data.state === "FL",
      is_eq_cat: input.data.eq_sub_zone === "CA",
      is_frame: [
        "Wood - Light Wood Frame",
        "Wood - Wood Frame (Modern)",
      ].includes(input.data.const__desc_ping),
      high_hazard_flood: false,
      low_hazard_flood: input.data.fema_flood_zone === "X",
      is_tricounty: [
        "broward",
        "miami-dade",
        "miami dade",
        "palm beach",
      ].includes(input.data.county?.toLowerCase()),
    };
  }
};

export const lngToMercatorX = (lng: number) => (lng * 20037508.34) / 180;

export const latToMercatorY = (lat: number) => {
  const rad =
    (Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / Math.PI) * 180;
  return (rad * 20037508.34) / 180;
};

export const calculateIntermediatePoints = (
  start: LatLngType,
  end: LatLngType,
  numPoints: number,
): LatLngType[] => {
  const points: LatLngType[] = [];

  // Include start point
  points.push(start);

  // Calculate intermediate points
  for (let i = 1; i < numPoints - 1; i++) {
    const fraction = i / (numPoints - 1);

    const lat = start.lat + (end.lat - start.lat) * fraction;
    const lng = start.lng + (end.lng - start.lng) * fraction;

    points.push({ lat, lng });
  }

  // Include end point
  points.push(end);

  return points;
};
